export default {
  en: {
    title: 'Reporting',
    menu: {
      compliance: 'Compliance',
      activity: 'Activity',
      performance: 'Performance',
      risk: 'Risk',
    },
    pipelineStatusFilter: {
      name: 'Pipeline status',
    },
    card: {
      error: 'An error occurred while fetching this metric',
      retry: 'Retry',
    },
    averageCompliance: {
      title: 'Average compliance',
      description: 'Average compliance of all suppliers with complete assessments.',
      noChangeDays: 'No change in the past 1 day | No change in the past {n} days',
      changeDays: 'in the past 1 day | in the past {n} days',
      noChange: 'No change in the {0}',
      change: 'in the {0}',
      suppliers:
        '<b>{n}</b> supplier with a completed assessment, out of {total} total. | <b>{n}</b> suppliers with completed assessments, out of {total} total.',
    },
    complianceBreakdown: {
      title: 'Compliance breakdown',
      description: 'The number of suppliers in different compliance intervals.',
      xLabel: 'Compliance percentage',
      yLabel: 'Number of suppliers',
    },
    complianceByCriticality: {
      title: 'Compliance by criticality',
      description: 'Average compliance of suppliers with each criticality tag.',
    },
    complianceByConfidentiality: {
      title: 'Compliance by confidentiality',
      description: 'Average compliance of suppliers with each confidentiality tag.',
    },
    complianceByPersonalData: {
      title: 'Compliance by personal data',
      description: 'Average compliance of suppliers with each personal data tag.',
    },
    complianceByDomain: {
      title: 'Compliance by domain',
      description: 'Average compliance per domain across all supplier answers.',
      nonCompliant: '{count} non-compliant',
    },
    leastCompliantSuppliers: {
      title: 'Least compliant suppliers',
      description: 'Your least compliant suppliers in ascending order.',
    },
    complianceByLabel: {
      title: 'Compliance by labels',
      description: 'Average compliance of suppliers with each custom label.',
    },
    barChart: {
      suppliers: '{count} suppliers | {count} supplier | {count} suppliers',
    },
    timeMetric: {
      bucketLabel: '{0}-{1} days',
      xLabel: 'Time',
      yLabel: 'Number of suppliers',
      format: {
        noDurations: 'None',
        daysHours: '{0}d {1}h',
        hoursMinutes: '{0}h {1}m',
      },
    },
    page: {
      period: 'Period: ',
      allTime: 'All time',
      noSuppliersTitle: 'No suppliers have completed their assessment yet.',
      noSuppliersSubtitle: 'Their compliance will appear here when they do.',
      filters: {
        lastMonth: 'Last 30 days',
        lastWeek: 'Last 7 days',
        lastQuarter: 'Last quarter',
      },
    },
    compliance: {
      export: {
        dropdown: 'Export',
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again.',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Compliance_{from}_to_{to}.csv',
        dateFormat: 'yyyy-MM-dd',
        avgComp: 'Average compliance of all suppliers with completed assessment',
        compAss: 'Completed assessments',
        newCompAss: 'New completed assessments for time period',
        totNumSuppliers: 'Total number of suppliers',
        name: 'Name',
        compliance: 'Compliance',
        delta: 'Change for time period',
        supplierCount: 'Supplier count',
        breakdown: 'Compliance percentage for suppliers with completed assessments',
      },
    },
    activity: {
      newSuppliers: 'New supplier | New suppliers',
      newSuppliersHelp: 'Total number of new suppliers connected to.',
      assessmentsCompleted: 'Completed assessment | Completed assessments',
      assessmentsCompletedHelp: 'Total number of initial assessments completed by suppliers.',
      sentInvitesAndCR: 'Sent invites & connection requests',
      sentInvitesAndCRHelp: 'The sum of invites and connection requests sent to suppliers.',
      acceptedInvitesAndCR: 'Accepted invites & connection requests',
      acceptedInvitesAndCRHelp: 'The sum of invites and connection requests accepted by suppliers.',
      suppliersReviewed: 'Supplier marked as reviewed | Suppliers marked as reviewed',
      suppliersReviewedHelp:
        'Total number of reviews completed for suppliers, broken down by the recommendation given.',
      recommendation: {
        none: 'Recommendation: None',
        approve: 'Recommendation: Approve',
        reject: 'Recommendation: Reject',
      },
      invitesSent: 'Invites sent',
      invitesAccepted: 'Invites accepted',
      crSent: 'Connection requests sent',
      crAccepted: 'Connection requests accepted',
      dateFormat: 'MMMM d, YYYY',
      export: {
        dropdown: 'Export',
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again.',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Activity_{from}_to_{to}.csv',
        newSuppliers: 'New suppliers',
        assessmentsCompleted: 'Completed assessments',
        invitesSent: 'Invites sent',
        invitesAccepted: 'Invites accepted',
        connectionRequestsSent: 'Connection requests sent',
        outgoingConnectionRequestsAccepted: 'Outgoing connection requests accepted',
        suppliersReviewed: 'Suppliers marked as reviewed',
        dateFormat: 'yyyy-MM-dd',
      },
    },
    perf: {
      aggregate: 'Aggregate',
      min: 'Minimum',
      max: 'Maximum',
      mean: 'Mean',
      median: 'Median',
      requestToReviewed: '{aggregate} time from invite/request to reviewed',
      requestToReviewedHelp:
        'The aggregated length of time between inviting or requesting a supplier and their first review (approval or rejection). Includes all suppliers reviewed within the time period.',
      connectionRequestToReviewed: 'Connection request to reviewed',
      inviteToReviewed: 'Invite sent to reviewed',
      requestToAssessmentComplete: '{aggregate} time from invite/request to assessment complete',
      requestToAssessmentCompleteHelp:
        'The aggregated length of time between inviting or requesting a supplier and the completion of their initial assessment. Includes all suppliers that completed their assessment within the time period.',
      connectionRequestToAssessmentComplete: 'Connection request to assessment complete',
      inviteToAssessmentComplete: 'Invite sent to assessment complete',
      requestToConnected: '{aggregate} time from invite/request to connected',
      requestToConnectedHelp:
        'The aggregated length of time between inviting or requesting a supplier and the acceptance of the invite or request. Includes all suppliers that were connected to within the time period.',
      connectionRequestToConnected: 'Connection request to connected',
      inviteToConnected: 'Invite sent to connected',
      assessmentCompleteToReviewed: '{aggregate} time from assessment complete to reviewed',
      assessmentCompleteToReviewedHelp:
        "The aggregated length of time between the completion of a supplier's initial assessment and their first review (approval or rejection). Includes all suppliers reviewed within the time period.",
      export: {
        dropdown: 'Export',
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again.',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        mean: 'Mean minutes',
        median: 'Median minutes',
        min: 'Minimum minutes',
        max: 'Maximum minutes',
        dateFormat: 'yyyy-MM-dd',
        fileName: 'Risk_Ledger_Performance_{from}_to_{to}.csv',
        requestToReviewed: 'Invite/request to reviewed',
        connectionRequestToReviewed: 'Connection request to reviewed',
        inviteToReviewed: 'Invite to reviewed',
        requestToAssessmentComplete: 'Invite/request to assessment complete',
        connectionRequestToAssessmentComplete: 'Connection request to assessment complete',
        inviteToAssessmentComplete: 'Invite to assessment complete',
        requestToConnected: 'Invite/request to connected',
        connectionRequestToConnected: 'Connection request to connected',
        inviteToConnected: 'Invite to connected',
        assessmentCompleteToReviewed: 'Assessment complete to reviewed',
      },
    },
    risk: {
      export: {
        button: 'Export CSV',
        pdfButton: 'Export PDF',
        failed: 'Export failed. Please try again.',
        from: 'From',
        to: 'To',
        metric: 'Metric',
        value: 'Value',
        fileName: 'Risk_Ledger_Risk_{from}_to_{to}.csv',
        dateFormat: 'yyyy-MM-dd',
      },
      openRisks: {
        title: 'Open risks',
      },
      closedRisks: {
        title: 'Closed risks',
      },
      assignedRisks: {
        title: 'Assigned risks',
      },
      riskMatrix: {
        title: 'Risk matrix',
        description: 'These are the number of open risks by impact and likelihood scores.',
        cellLabel:
          'No open risks, impact {impact}, likelihood {likelihood} | 1 open risk, impact {impact}, likelihood {likelihood} | {count} open risks, impact {impact}, likelihood {likelihood}',
      },
      viewAllRisks: 'View all risks',
      risksBySuppliers: {
        title: 'Top ten suppliers by number of open risks',
        description: 'These are the top ten suppliers with the highest number of open risks.',
      },
      viewAllSuppliers: 'View all suppliers',
      risks: '{n} risk | {n} risks',
      risksByControls: {
        title: 'Top ten controls by number of open risks',
        description:
          'These are the top ten controls among all your suppliers based on the number of open risks.',
        unknownControl: 'Unknown control',
      },
      risksByCriticality: {
        title: 'Open risks by supplier criticality',
        description: 'The number of open risks of suppliers with each criticality tag.',
      },
      risksByConfidentiality: {
        title: 'Open risks by supplier confidentiality',
        description: 'The number of open risks of suppliers with each confidentiality tag.',
      },
      risksByPII: {
        title: 'Open risks by supplier personal data',
        description: 'The number of open risks of suppliers with each personal data tag.',
      },
      risksByLabel: {
        title: 'Open risks by supplier labels',
        description: 'The number of open risks of suppliers with each custom label.',
      },
    },
    timePeriodSelector: {
      timePeriod: '{from} - {to}',
      allTime: 'All time',
      lastQuarter: 'Past 90 days',
      lastMonth: 'Past 30 days',
      lastWeek: 'Past 7 days',
      custom: 'Custom',
      apply: 'Apply',
      cancel: 'Cancel',
      incompleteRange: 'Select a start and end date',
    },
  },
};
